/**
 * Lo que contestaron las personas, de una campaña (`campanaId`) o de todas las del comercio (con un selector de
 * campaña). El texto llega ya sin HTML ni la cita del mail original (el backend lo limpia): acá se muestra como texto,
 * nunca como HTML. Búsqueda, tipo, campaña y página en la URL (`?rq=&rt=&rc=&rp=`), con nombres propios para no chocar
 * con la tabla de personas en el detalle de una campaña.
 *
 * El tipo separa las que piden la baja de las demás: las demás son las que hay que leer y contestar.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageSquareReply, Search } from "lucide-react";
import { emailsApi } from "../api";
import type { CampanaFila, PaginaRespuestas, TipoRespuesta } from "../types";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { num, plural } from "../../campana-nueva/utils/format";
import { R_EMAILS, asuntoLegible, diaMesHora } from "../logic/emails";
import { useEstadoEmails } from "../contexto";

const POR_PAGINA = 30;

const TIPOS: { valor: "" | TipoRespuesta; clave: "todas" | TipoRespuesta; texto: string }[] = [
  { valor: "", clave: "todas", texto: "Todas" },
  { valor: "otras", clave: "otras", texto: "Las demás" },
  { valor: "bajas", clave: "bajas", texto: "Piden la baja" },
];
const esTipo = (v: string | null): v is TipoRespuesta => v === "bajas" || v === "otras";

function Respuesta({ id, texto }: { id: string; texto: string }) {
  const [abierta, setAbierta] = useState(false);
  const largo = texto.length > 320;
  const idTexto = `respuesta-${id}`;
  return (
    <div>
      <p id={idTexto} className={`whitespace-pre-wrap break-words text-sm text-slate-800 ${!abierta && largo ? "line-clamp-4" : ""}`}>{texto || <span className="text-slate-400">(sin texto)</span>}</p>
      {largo && (
        <button type="button" onClick={() => setAbierta((v) => !v)} aria-expanded={abierta} aria-controls={idTexto} className="mt-1 text-xs font-semibold text-violet-700 hover:underline">
          {abierta ? "Ver menos" : "Ver todo"}
        </button>
      )}
    </div>
  );
}

export function ListaRespuestas({ campanaId, campanas }: { campanaId?: number; campanas?: CampanaFila[] }) {
  const [sp, setSp] = useSearchParams();
  const { version } = useEstadoEmails();
  const q = sp.get("rq") || "";
  const tipoUrl = sp.get("rt");
  const tipo = esTipo(tipoUrl) ? tipoUrl : undefined;
  const pagina = Math.max(1, Number.parseInt(sp.get("rp") || "1", 10) || 1);
  const rc = Number.parseInt(sp.get("rc") || "", 10);
  const campana = campanaId ?? (Number.isFinite(rc) && rc > 0 ? rc : undefined);

  const [texto, setTexto] = useState(q);
  const [datos, setDatos] = useState<PaginaRespuestas | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [reintento, setReintento] = useState(0);
  const seq = useRef(0);
  const idBuscar = useId();
  const idCampana = useId();

  const cambiar = useCallback((k: string, v: string) => {
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      if (v) n.set(k, v); else n.delete(k);
      if (k !== "rp") n.delete("rp");
      return n;
    }, { replace: true });
  }, [setSp]);

  useEffect(() => { setTexto(q); }, [q]);
  useEffect(() => {
    if (texto.trim() === q) return;
    const t = setTimeout(() => cambiar("rq", texto.trim().slice(0, 80)), 400);
    return () => clearTimeout(t);
  }, [texto, q, cambiar]);

  useEffect(() => {
    const mio = ++seq.current;
    setCargando(true);
    void (async () => {
      let nuevo: PaginaRespuestas | null = null;
      let err = "";
      let cod = "";
      try {
        const l = leerRespuesta<PaginaRespuestas>(await emailsApi.respuestas({ campana, q: q || undefined, tipo, pagina, porPagina: POR_PAGINA }));
        if (!l.ok || !l.data) err = l.ok ? "El servidor no devolvió las respuestas." : textoError(l);
        else nuevo = l.data;
      } catch (e) {
        const x = leerError(e);
        err = textoError(x);
        cod = x.code;
      }
      if (mio !== seq.current) return;
      setCargando(false);
      setError(err);
      if (nuevo) setDatos(nuevo);
      // la campaña dejó de ser de este comercio: no se siguen mostrando sus respuestas
      else if (cod === "NOT_FOUND") setDatos(null);
    })();
  }, [campana, q, tipo, pagina, reintento, version]);

  const total = datos?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  // una campaña de la URL que no está en el selector (vieja, o de otro lado): se ve cuál es, no «Todas»
  const fueraDeLaLista = campanaId === undefined && campana !== undefined && !(campanas || []).some((c) => c.id === campana);
  const filtrado = !!q || !!tipo;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Qué respuestas ver">
        {TIPOS.map((t) => {
          const activo = (tipo || "") === t.valor;
          const n = datos?.porTipo?.[t.clave];
          return (
            <button key={t.clave} type="button" aria-pressed={activo} onClick={() => cambiar("rt", t.valor)}
              className={`h-8 px-3 rounded-full text-xs font-semibold border ${activo ? "border-violet-600 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {t.texto}{n !== undefined ? <span className={`ml-1.5 tabular-nums ${activo ? "text-violet-100" : "text-slate-400"}`}>{num(n)}</span> : null}
            </button>
          );
        })}
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <label htmlFor={idBuscar} className="sr-only">Buscar en las respuestas</label>
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input id={idBuscar} value={texto} maxLength={80} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar en lo que contestaron, el nombre o el mail"
            className="w-full h-10 rounded-xl border border-slate-200 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </div>
        {campanaId === undefined && ((campanas && campanas.length > 1) || fueraDeLaLista) && (
          <div>
            <label htmlFor={idCampana} className="block text-[11px] font-semibold text-slate-500">Campaña</label>
            <select id={idCampana} value={campana ? String(campana) : ""} onChange={(e) => cambiar("rc", e.target.value)}
              className="mt-1 h-10 max-w-[260px] rounded-xl border border-slate-200 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
              <option value="">Todas</option>
              {fueraDeLaLista && <option value={String(campana)}>Campaña {campana}</option>}
              {(campanas || []).map((c) => <option key={c.id} value={String(c.id)}>{c.titulo || `Campaña ${c.id}`}</option>)}
            </select>
          </div>
        )}
      </div>

      {cargando && !datos ? (
        <Esqueleto alto="h-48" etiqueta="Cargando respuestas" />
      ) : error && !datos ? (
        <ErrorTarjeta texto={`No pudimos traer las respuestas. ${error}`} onReintentar={() => setReintento((n) => n + 1)} />
      ) : !datos || !datos.total ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
          <MessageSquareReply size={22} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-600">{filtrado ? "Ninguna respuesta coincide con la búsqueda o el filtro." : "Todavía nadie contestó."}</p>
        </div>
      ) : (
        <div className={`space-y-3 ${cargando ? "opacity-60" : ""}`} aria-busy={cargando || undefined}>
          {error && <p className="text-xs text-amber-700">{error} Se muestra lo último que trajimos.</p>}
          {!datos.filas.length ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center text-sm text-slate-600">
              Esta página ya no tiene respuestas.{" "}
              <button type="button" onClick={() => cambiar("rp", "")} className="font-semibold text-violet-700 underline">Ir a la primera</button>
            </div>
          ) : (
            <ul className="space-y-3">
              {datos.filas.map((r) => (
                <li key={r.id} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">
                        {r.nombre || "Sin nombre"} <span className="font-normal text-xs text-slate-500 break-all">{r.email}</span>
                        {r.pideBaja && <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700">Pide la baja</span>}
                      </p>
                      <p className="text-xs text-slate-400">
                        {r.asunto ? asuntoLegible(r.asunto) : "Sin asunto"}
                        {campanaId === undefined && r.campana.titulo && (
                          <> · <Link to={`${R_EMAILS}/campanas/${encodeURIComponent(String(r.campana.id))}`} className="text-violet-700 hover:underline">{r.campana.titulo}</Link></>
                        )}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500 whitespace-nowrap">{diaMesHora(r.recibidaEn)}</span>
                  </div>
                  <div className="mt-2"><Respuesta id={r.id} texto={r.texto} /></div>
                </li>
              ))}
            </ul>
          )}
          {(paginas > 1 || pagina > 1) && (
            <nav aria-label="Páginas" className="flex items-center justify-between gap-3 text-sm">
              <button type="button" onClick={() => cambiar("rp", pagina - 1 > 1 ? String(pagina - 1) : "")} disabled={pagina <= 1}
                className="h-9 px-3 rounded-xl border border-slate-200 bg-white font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">Anterior</button>
              <span className="text-slate-600">Página {num(pagina)} de {num(paginas)} · {num(total)} {plural(total, "respuesta", "respuestas")}</span>
              <button type="button" onClick={() => cambiar("rp", String(pagina + 1))} disabled={pagina >= paginas}
                className="h-9 px-3 rounded-xl border border-slate-200 bg-white font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">Siguiente</button>
            </nav>
          )}
        </div>
      )}
    </div>
  );
}

export default ListaRespuestas;
